import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Building2, 
  LogOut,
  CheckSquare,
  BarChart3,
  Database,
  Cog,
  LayoutTemplate,
  Palette,
  Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { getBranding } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLayout = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [branding, setBranding] = useState({ app_name: 'UN-IT Forms' });

  useEffect(() => {
    getBranding().then(setBranding).catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  const getLogoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  };

  const navigation = [
    { name: t('nav.dashboard'), path: '/admin/dashboard', icon: LayoutDashboard, roles: ['super_admin', 'department_head', 'deputy', 'user'] },
    { name: t('nav.forms'), path: '/admin/forms', icon: FileText, roles: ['super_admin', 'department_head', 'deputy', 'user'] },
    { name: t('nav.approvals'), path: '/admin/approvals', icon: CheckSquare, roles: ['super_admin', 'department_head', 'deputy'] },
    { name: t('nav.submissions'), path: '/admin/submissions', icon: BarChart3, roles: ['super_admin', 'department_head', 'deputy', 'user'] },
    { name: t('nav.departments'), path: '/admin/departments', icon: Building2, roles: ['super_admin'] },
    { name: t('nav.users'), path: '/admin/users', icon: Users, roles: ['super_admin', 'department_head'] },
    { name: t('nav.templates'), path: '/admin/templates', icon: LayoutTemplate, roles: ['super_admin'] },
    { name: t('nav.globalLists'), path: '/admin/global-lists', icon: Database, roles: ['super_admin'] },
    { name: t('nav.branding'), path: '/admin/branding', icon: Palette, roles: ['super_admin'] },
    { name: t('nav.security'), path: '/admin/security', icon: Shield, roles: ['super_admin'] },
    { name: t('nav.settings'), path: '/admin/settings', icon: Cog, roles: ['super_admin'] }
  ];

  const filteredNavigation = navigation.filter(item => item.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200">
            <div className="flex items-center gap-2">
              {branding.app_logo_light ? (
                <img src={getLogoUrl(branding.app_logo_light)} alt={branding.app_name} className="h-8" />
              ) : (
                <h1 className="text-xl font-bold text-gray-900">{branding.app_name}</h1>
              )}
            </div>
            <LanguageSwitcher />
          </div>

          {/* User Info */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="text-sm font-medium text-gray-900">{user?.full_name || user?.username}</div>
            <div className="text-xs text-gray-500 mt-1">
              {user?.role === 'super_admin' && 'Super Administrator'}
              {user?.role === 'department_head' && 'Abteilungsleiter'}
              {user?.role === 'deputy' && 'Stellvertreter'}
              {user?.role === 'user' && 'Benutzer'}
            </div>
            {user?.department_name && <div className="text-xs text-gray-500 mt-1">{user.department_name}</div>}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${active ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-gray-200">
            <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-gray-900" onClick={handleLogout}>
              <LogOut className="w-5 h-5 mr-3" />
              {t('nav.logout')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64">
        <div className="p-8">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
